import { useParams, Link, Navigate } from "@/lib/router-compat";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { projectBySlugQuery, LOAD_ERROR_MESSAGE } from "@/lib/publicQueries";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Camera, Video, Share2, Calendar, User, Briefcase, ExternalLink } from "lucide-react";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema } from "@/components/StructuredData";
import { trackPortfolioView } from "@/lib/analytics";
import ProjectGallery from "@/components/ProjectGallery";
import RelatedProjects from "@/components/RelatedProjects";
import { storageImage, storageSrcSet } from "@/lib/imageUrl";
import { extractDriveFileId, getDrivePreviewUrl, getDriveViewUrl } from "@/lib/googleDriveHelpers";

const ProjectDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: project = null, isLoading: loading, isError } = useQuery(projectBySlugQuery(slug ?? ""));
  const [fallbackEmbeds, setFallbackEmbeds] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (project) {
      trackPortfolioView(project.id, project.title);
    }
  }, [project?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Lädt...</p>
      </div>
    );
  }

  if (!project && isError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <p role="alert" className="text-center text-muted-foreground">{LOAD_ERROR_MESSAGE}</p>
      </div>
    );
  }

  if (!project) {
    return <Navigate to="/portfolio" replace />;
  }

  const categoryIcons = {
    photography: Camera,
    videography: Video,
    "social-media": Share2,
  };

  const Icon = categoryIcons[project.category];

  return (
    <>
      <SEOHead
        title={`${project.title} - ${project.category === "photography" ? "Fotografie" : project.category === "videography" ? "Videoproduktion" : "Social Media"} | Clicks DMP`}
        description={project.description || `${project.title} - Professionelle ${project.category === "photography" ? "Fotografie" : project.category === "videography" ? "Videoproduktion" : "Social Media Content"} von Clicks DMP Mannheim`}
        canonical={`https://clicksdmp.com/portfolio/${project.slug}`}
        ogImage={project.coverImage}
        schema={
          <BreadcrumbSchema
            items={[
              { name: "Startseite", url: "https://clicksdmp.com/" },
              { name: "Portfolio", url: "https://clicksdmp.com/portfolio" },
              { name: project.title, url: `https://clicksdmp.com/portfolio/${project.slug}` },
            ]}
          />
        }
      />
      <div className="min-h-screen bg-background">
        {/* Hero Header with Cover Image */}
        <header className="relative min-h-[60vh] md:min-h-[70vh] flex items-center justify-center overflow-hidden border-b border-border">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src={(() => {
                const raw = project.coverImage?.startsWith('video::') 
                  ? (project.images?.find((img: string) => !img.startsWith('video::')) || project.coverImage)
                  : project.coverImage;
                if (!raw) return '';
                
                // Use thumbnail for hero background for faster loading
                const isDrive = raw.includes('drive.google.com');
                if (isDrive) {
                  const id = extractDriveFileId(raw);
                  // Google Drive media is not shown on public pages (no request to Google before consent)
                  return '';
                }
                return storageImage(raw, 1600, { quality: 78 });
              })()}
              alt={project.title}
              className="w-full h-full object-cover"
              fetchPriority="high"
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
          </div>

          {/* Content */}
          <div className="relative z-10 container-custom pt-24 pb-16">
            <Link to="/portfolio">
              <Button variant="ghost" size="sm" className="gap-2 mb-8 bg-background/50 backdrop-blur-xs hover:bg-background/80">
                <ArrowLeft className="w-4 h-4" />
                Zurück zum Portfolio
              </Button>
            </Link>

            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-xs border border-border">
                  <Icon className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium capitalize">
                    {project.category.replace("-", " ")}
                  </span>
                </div>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {project.title}
              </h1>

              <p className="text-xl leading-relaxed">
                {project.description}
              </p>
            </div>
          </div>
        </header>

      {/* Project Details */}
      <section className="section-padding border-b border-border">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl">
            {project.client && (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Kunde</p>
                  <p className="font-medium">{project.client}</p>
                </div>
              </div>
            )}

            {project.year && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Jahr</p>
                  <p className="font-medium">{project.year}</p>
                </div>
              </div>
            )}

            {project.services && project.services.length > 0 && (
              <div className="flex items-start gap-3 md:col-span-3">
                <Briefcase className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Leistungen</p>
                  <div className="flex flex-wrap gap-2">
                    {project.services.map((service) => (
                      <span
                        key={service}
                        className="px-3 py-1 rounded-full bg-secondary text-sm"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Project Story */}
      <section className="section-padding border-b border-border">
        <div className="container-custom max-w-3xl">
          <h2 className="text-3xl font-bold mb-6">Projekt-Geschichte</h2>
          <p className="text-muted-foreground leading-relaxed text-lg">
            {project.fullDescription}
          </p>
        </div>
      </section>

      {/* Project Images */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold mb-12 text-center">
            {project.category === "videography" 
              ? "Video-Galerie" 
              : project.category === "social-media" 
              ? "Content-Galerie" 
              : "Visuelle Galerie"}
          </h2>
          
          {project.category === "videography" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
              {(project.images ?? []).map((image, index) => {
                const isVideo = image.startsWith('video::') || /\.(mp4|mov|avi|webm|mkv|m4v)(\?|$)/i.test(image) || image.includes('drive.google.com');
                const rawUrl = image.startsWith('video::') ? image.replace('video::', '') : image;

                const isDrive = rawUrl.includes('drive.google.com');
                const fileId = isDrive ? extractDriveFileId(rawUrl) : null;
                // Google Drive media is not shown on public pages (no request to Google before consent)
                if (isDrive) return null;

                return (
                  <div
                    key={index}
                    data-video-container
                    className="relative aspect-[4/3] overflow-hidden rounded-lg bg-card border border-border group"
                  >
                    {isVideo ? (
                      isDrive && fileId ? (
                        <div className="relative w-full h-full bg-black flex flex-col items-center justify-center p-4">
                          {fallbackEmbeds[index] ? (
                            <>
                              <iframe
                                src={getDrivePreviewUrl(fileId)!}
                                className="w-full h-[85%] rounded-sm"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen
                              />
                              <div className="mt-2 flex gap-2">
                                <Button
                                  onClick={() => window.open(getDriveViewUrl(fileId)!, '_blank')}
                                  size="sm"
                                  variant="secondary"
                                  className="gap-2"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Open in Drive
                                </Button>
                              </div>
                            </>
                          ) : (
                            <iframe
                              src={getDrivePreviewUrl(fileId)!}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                              allowFullScreen
                              onError={() => {
                                console.error('Google Drive iframe failed to load:', fileId);
                                setFallbackEmbeds((prev) => ({ ...prev, [index]: true }));
                              }}
                            />
                          )}
                        </div>
                      ) : (
                        <video
                          src={rawUrl}
                          className="w-full h-full object-contain bg-black"
                          controls
                          controlsList="nodownload"
                          preload="metadata"
                          onContextMenu={(e) => e.preventDefault()}
                        >
                          Ihr Browser unterstützt das Video-Tag nicht.
                        </video>
                      )
                    ) : (
                      <img
                        src={storageImage(image, 800)}
                        srcSet={storageSrcSet(image, [480, 800, 1200])}
                        sizes="(max-width: 768px) 100vw, 50vw"
                        alt={`${project.title} - Image ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                        decoding="async"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable={false}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <ProjectGallery 
              images={project.images ?? []} 
              projectTitle={project.title}
            />
          )}
        </div>
      </section>

      {/* Related Projects */}
      <RelatedProjects 
        currentProjectId={project.id} 
        currentCategory={project.category}
      />

      {/* CTA Section */}
      <section className="section-padding border-t border-border bg-gradient-to-b from-background to-secondary/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Bereit für Ihr Projekt?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Lassen Sie uns gemeinsam etwas Außergewöhnliches kreieren. Buchen Sie ein kostenloses Erstgespräch, um Ihre Vision zu besprechen.
          </p>
          <Link to="/">
            <Button size="lg" className="gap-2">
              <Calendar className="w-5 h-5" />
              Kostenloses Erstgespräch buchen
            </Button>
          </Link>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
};

export default ProjectDetail;
